import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  Button,
  Collapse,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  TextField,
  InputAdornment,
  LinearProgress,
  InputLabel,
  Autocomplete,
  ClickAwayListener,
  IconButton,
  Fab,
  Tooltip,
  Snackbar,
  Slide,
  Fade,
  Skeleton,
  TableSortLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Share as ShareIcon,
  Sync as SyncIcon,
  FilterAltOutlined as FilterAltOutlinedIcon,
  DateRange as DateRangeIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  ContentCopy as ContentCopyIcon,
  ReceiptLong as ReceiptLongIcon,
  Notes as NotesIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  AttachMoney as AttachMoneyIcon,
  Verified as VerifiedIcon,
  Savings as SavingsIcon,
  Tune as TuneIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useTheme } from "@mui/material/styles";
import { Pagination } from "../components/common/Pagination";
import LoadingTimerDebug from "../components/common/LoadingTimerDebug";
import FloatingActionMenu from "../components/common/FloatingActionMenu";
import CategoryIconBadge from "../components/transactions/CategoryIconBadge";
import { ShareRecordModal } from "../components/groups";
import PageShareModal from "../components/common/PageShareModal";
import { useShareAction } from "../contexts/ShareActionContext";
import { Group } from "../lib/types";
import {
  plaidService,
  PlaidTransaction,
  PlaidAccount,
  PlaidTransactionsCacheStatus,
  organizationService,
  Organization,
} from "../services/external";
import {
  US_FEDERAL_TAX_CATEGORIES,
  OREGON_STATE_TAX_CATEGORIES,
} from "../lib/constants";
import { demoService } from "../services/demo/demoService";
import {
  transactionCategoriesDB,
  TransactionCategory,
  LearnedPattern,
} from "../services/database/transactionCategoriesDB";
import { debtAPI, groupAPI, shareAPI } from "../services/api";
import type { DebtPayment } from "../lib/types";
import { settingsService } from "../services/settings/settingsService";
import * as expenseUtils from "../lib/expenseUtils";
import { UserCategorizationModal } from "../components/transactions/UserCategorizationModal";
import { getTransactionCategoryIconKey } from "../lib/transactionCategoryIcons";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

ChartJS.register(ArcElement, ChartTooltip, Legend);

// Plaid Icon Component (unused - removed to fix linting)

interface TransactionWithCategories extends PlaidTransaction {
  general_category?: string;
  category_icon?: string;
  federal_category?: string;
  oregon_category?: string;
  custom_description?: string;
  ai_confidence?: string;
  needs_user_input?: boolean;
}

// A saved set of transaction filters the user can re-apply later.
interface TxnFilterProfileFilters {
  searchTerm: string;
  selectedAccount: string;
  selectedOrganization: string;
  selectedInstitution: string;
  selectedAccountType: string;
  amountMin: string;
  amountMax: string;
  filterGeneralCategory: string;
  filterFederalCategory: string;
  filterOregonCategory: string;
  sharedOnly: boolean;
}

interface TxnFilterProfile {
  id: string;
  name: string;
  filters: TxnFilterProfileFilters;
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatPlaidSyncTimestamp = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "MMM do, ''yy 'at' h:mm a");
};

const formatTransactionDateLines = (dateString: string) => {
  const date = new Date(dateString + "T12:00:00");
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const weekday = date.toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });
  const month = date.toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  const day = date.getUTCDate();
  const getOrdinalSuffix = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };
  const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
  const year = date.getFullYear();

  let topLine = weekday;
  if (isSameDay(date, today)) {
    topLine = "Today";
    console.log("[DEBUG] Using relative date label:", {
      dateString,
      label: "Today",
    });
  } else if (isSameDay(date, yesterday)) {
    topLine = "Yesterday";
    console.log("[DEBUG] Using relative date label:", {
      dateString,
      label: "Yesterday",
    });
  }

  return {
    topLine,
    monthDay: `${month} ${dayWithSuffix}`,
    year: String(year),
  };
};

const formatTransactionTime = (transaction: {
  datetime?: string | null;
  authorized_datetime?: string | null;
  date: string;
}) => {
  // Most Plaid/manual transactions are date-only (no time component). Only show
  // a time when the feed actually provides one — otherwise return "" so we
  // don't render a misleading "12:00 PM" for every row.
  const rawDateTime = transaction.datetime || transaction.authorized_datetime;
  if (!rawDateTime) return "";
  const date = new Date(rawDateTime);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

// Tax-category values are stored inconsistently as either the category id or
// its name. Resolve to the canonical name so filtering can compare reliably.
const resolveTaxName = (
  stored: string | undefined,
  list: { id: string; name: string }[]
): string => {
  if (!stored) return "";
  const match = list.find((c) => c.id === stored || c.name === stored);
  return match ? match.name : stored;
};

// Human-readable date-range label for the Total Deposits card, e.g.
// "Jan 1, 2025 – Jun 3, 2026". Inputs are YYYY-MM-DD strings.
const formatRangeLabel = (start: string, end: string): string => {
  const fmt = (s: string) => {
    if (!s) return "";
    const d = new Date(`${s}T12:00:00`);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const a = fmt(start);
  const b = fmt(end);
  if (a && b) return `${a} – ${b}`;
  return a || b || "";
};

// Resolve an account's institution name. `institution` is normally a
// PlaidInstitution object, but some sources/fixtures store it as a plain
// string — handle both.
const resolveInstitutionName = (
  account: { institution?: unknown } | null | undefined
): string => {
  const inst = account?.institution as
    | { name?: string }
    | string
    | null
    | undefined;
  if (!inst) return "";
  if (typeof inst === "string") return inst;
  return inst.name || "";
};

// Map of well-known institution names → website domain, used to derive a
// favicon when Plaid returns no logo (it returns null for several major
// banks, notably Chase).
const INSTITUTION_DOMAIN_FALLBACKS: Array<{ match: RegExp; domain: string }> = [
  { match: /chase/i, domain: "chase.com" },
  { match: /bank of america/i, domain: "bankofamerica.com" },
  { match: /wells fargo/i, domain: "wellsfargo.com" },
  { match: /capital one/i, domain: "capitalone.com" },
  { match: /citi/i, domain: "citi.com" },
  { match: /us bank|u\.s\. bank/i, domain: "usbank.com" },
  { match: /selco/i, domain: "selco.org" },
];

// Resolve an icon URL for an account's institution: Plaid base64 logo first,
// then a favicon derived from the institution URL, then a favicon from a
// known-bank domain map. Returns null when no icon can be derived.
const resolveInstitutionIconSrc = (
  account: { institution?: unknown } | null | undefined
): string | null => {
  const inst = account?.institution as
    | { name?: string; logo?: string | null; url?: string | null }
    | string
    | null
    | undefined;
  if (!inst) return null;
  if (typeof inst !== "string") {
    if (inst.logo) return `data:image/png;base64,${inst.logo}`;
    if (inst.url) {
      try {
        const domain = new URL(inst.url).hostname;
        console.log("[TransactionsPage] Institution favicon from url", {
          name: inst.name,
          domain,
        });
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
      } catch {
        // fall through to name-based lookup
      }
    }
  }
  const name = typeof inst === "string" ? inst : inst.name || "";
  const fallback = INSTITUTION_DOMAIN_FALLBACKS.find((f) => f.match.test(name));
  if (fallback) {
    console.log("[TransactionsPage] Institution favicon from domain map", {
      name,
      domain: fallback.domain,
    });
    return `https://www.google.com/s2/favicons?domain=${fallback.domain}&sz=64`;
  }
  return null;
};

const formatDisplayLabel = (value: string | undefined) => {
  if (!value) return "";
  return value
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const normalizeAllCapsWords = (value: string) => {
  const formatted = value.replace(/\b[A-Z]{2,}\b/g, (word) => {
    return word.charAt(0) + word.slice(1).toLowerCase();
  });
  if (formatted !== value) {
    console.log("[DEBUG] Normalized all-caps label:", {
      original: value,
      formatted,
    });
  }
  return formatted;
};

const getTransactionTypeLabel = (transaction: PlaidTransaction) => {
  // Plaid convention: positive amount = money OUT (debit), negative = money IN (credit)
  if (transaction.amount > 0) return "Debit";
  if (transaction.amount < 0) return "Credit";
  const rawType = (transaction.transaction_type || "").toLowerCase();
  if (rawType === "debit") return "Debit";
  if (rawType === "credit") return "Credit";
  return "—";
};

const SlideUp = (props: any) => <Slide {...props} direction="up" />;

const isPlaidSyncedTransaction = (transaction: PlaidTransaction) => {
  if (!transaction.transaction_id) return false;
  return !transaction.transaction_id.startsWith("csv_");
};

const getPlaidTransactionJson = (transaction: TransactionWithCategories) => {
  const {
    general_category,
    federal_category,
    oregon_category,
    ai_confidence,
    needs_user_input,
    is_shared,
    isShared,
    ...plaidPayload
  } = transaction as TransactionWithCategories & {
    is_shared?: boolean;
    isShared?: boolean;
  };
  return JSON.stringify(plaidPayload, null, 2);
};

const getTaxCategoryName = (
  categoryId: string | undefined,
  categories: { id: string; name: string }[]
) => {
  if (!categoryId) return "";
  const match =
    categories.find((cat) => cat.id === categoryId) ||
    categories.find((cat) => cat.name === categoryId);
  return match?.name || formatDisplayLabel(categoryId);
};

const INCOME_GENERAL_CATEGORY = "Income";
const INCOME_TAX_CATEGORY = "income";
const TRANSFER_GENERAL_CATEGORY = "Transfer";
const TRANSFER_TAX_CATEGORY = "transfer";
// Plaid convention: positive amount = money OUT (debit), negative = money IN (credit)
const isCreditAmount = (amount: number) => amount < 0;
const isDebitAmount = (amount: number) => amount > 0;

// General categories from backend
const GENERAL_CATEGORIES = [
  "Accounting",
  "Airlines",
  "Apps and Games",
  "ATM Fees",
  "Auto Insurance",
  "Auto Maintenance",
  "Automotive",
  "Banking Fees",
  "Bars and Nightlife",
  "Beauty",
  "Books",
  "Books and Media",
  "Business Services",
  "Cable TV",
  "Charity",
  "Childcare",
  "Cleaning",
  "Clothing",
  "Coffee Shops",
  "Community",
  "Computer Hardware",
  "Consulting",
  "Court Fees",
  "Credit Cards",
  "Dental",
  "Department Stores",
  "DMV",
  "Education",
  "Electric",
  "Electronics",
  "Entertainment",
  "Family",
  "Fast Food",
  "Fitness",
  "Food and Drink",
  "Furniture",
  "Gaming",
  "Gas",
  "Gas Stations",
  "Gifts",
  "Government",
  "Groceries",
  "Hobbies",
  "Home and Garden",
  "Home Improvement",
  "Home Maintenance",
  "Hotels",
  "Income",
  "Insurance",
  "Internet",
  "Internet Service",
  "Investment",
  "Jewelry",
  "Legal",
  "Loans",
  "Medical",
  "Mental Health",
  "Mobile Phone",
  "Mortgage",
  "Movies",
  "Music",
  "Office Supplies",
  "Online Shopping",
  "Other",
  "Parking",
  "Personal Care",
  "Pet Care",
  "Pet Supplies",
  "Pharmacy",
  "Phone Service",
  "Professional Services",
  "Public Transportation",
  "Recreation",
  "Religious",
  "Rent",
  "Restaurants",
  "Rideshare",
  "Software",
  "Spa",
  "Specialty Retail",
  "Sports and Outdoors",
  "Sports Events",
  "Streaming Services",
  "Taxes",
  "Taxi",
  "Technology",
  "Tours and Activities",
  "Transfer",
  "Trash",
  "Travel",
  "Travel Insurance",
  "Utilities",
  "Vacation Rentals",
  "Vision",
  "Water",
];

const TransactionsPage: React.FC = () => {
  const theme = useTheme();
  const { setShareHandler } = useShareAction();
  const [transactions, setTransactions] = useState<TransactionWithCategories[]>(
    []
  );
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [customNames, setCustomNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTransaction, setShareTransaction] =
    useState<TransactionWithCategories | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sharedOnly, setSharedOnly] = useState(false);
  // transaction_id -> debt payment it was auto-matched to
  const [debtPaymentsByTxn, setDebtPaymentsByTxn] = useState<
    Map<string, DebtPayment>
  >(new Map());
  useEffect(() => {
    let cancelled = false;
    debtAPI
      .getPayments()
      .then((rows) => {
        if (cancelled) return;
        setDebtPaymentsByTxn(new Map(rows.map((r) => [r.transactionId, r])));
      })
      .catch((error) => console.error("Failed to load debt payments", error));
    return () => {
      cancelled = true;
    };
  }, []);
  const [pageShareOpen, setPageShareOpen] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiStatus, setAiStatus] = useState<string>("");
  const loadKeyRef = useRef<string | null>(null);
  const loadInFlightRef = useRef(false);
  const autoRefreshKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setShareHandler(() => {
      setPageShareOpen(true);
    });
    return () => setShareHandler(null);
  }, [setShareHandler]);

  // User teaching modal state
  const [showTeachingModal, setShowTeachingModal] = useState(false);
  const [teachingQueue, setTeachingQueue] = useState<
    TransactionWithCategories[]
  >([]);
  const [currentTeachingIndex, setCurrentTeachingIndex] = useState(0);
  const [learnedPatterns, setLearnedPatterns] = useState<LearnedPattern[]>([]);
  const [customGeneralCategories, setCustomGeneralCategories] = useState<
    string[]
  >([]);

  // Filtering and display
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  // Additional filters (org / institution / account-type / amount / categories)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [selectedInstitution, setSelectedInstitution] = useState<string>("");
  const [selectedAccountType, setSelectedAccountType] = useState<string>("");
  const [amountMin, setAmountMin] = useState<string>("");
  const [amountMax, setAmountMax] = useState<string>("");
  const [filterGeneralCategory, setFilterGeneralCategory] =
    useState<string>("");
  const [filterFederalCategory, setFilterFederalCategory] =
    useState<string>("");
  const [filterOregonCategory, setFilterOregonCategory] = useState<string>("");
  // Organizations (account -> org mapping), mirrors AccountsPage.
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [accountOrganizations, setAccountOrganizations] = useState<
    Record<string, string>
  >({});
  // Saved filter profiles (persisted to localStorage).
  const FILTER_PROFILES_KEY = "transaction_filter_profiles";
  const [filterProfiles, setFilterProfiles] = useState<TxnFilterProfile[]>(
    () => {
      try {
        const raw = localStorage.getItem("transaction_filter_profiles");
        return raw ? (JSON.parse(raw) as TxnFilterProfile[]) : [];
      } catch {
        return [];
      }
    }
  );
  const [activeProfileId, setActiveProfileId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>(() => {
    // Set to 24 months ago (Plaid's maximum for most institutions)
    const date = new Date();
    date.setMonth(date.getMonth() - 24);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [sortConfig, setSortConfig] = useState<{
    key:
      | "date"
      | "amount"
      | "description"
      | "general_category"
      | "federal_category"
      | "oregon_category";
    direction: "asc" | "desc";
  }>({ key: "date", direction: "desc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [copyToastOpen, setCopyToastOpen] = useState(false);
  const copyToastTimeoutRef = useRef<number | null>(null);
  const [includeTransfers, setIncludeTransfers] = useState(false);
  const [editingCell, setEditingCell] = useState<{
    transactionId: string;
    field: "general" | "federal" | "oregon";
  } | null>(null);
  const [editingDescriptionId, setEditingDescriptionId] = useState<
    string | null
  >(null);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState("");
  const [cacheStatus, setCacheStatus] = useState<PlaidTransactionsCacheStatus>({
    lastSyncedAt: null,
    refreshing: false,
  });
  const cachePollAttemptsRef = useRef(0);

  // Demo mode state for floating tooltip
  const [isDemoMode, setIsDemoMode] = useState(demoService.isDemo());
  const [showDemoTooltip, setShowDemoTooltip] = useState(true);

  // Subscribe to demo mode changes
  useEffect(() => {
    const unsubscribe = demoService.subscribe((isDemo) => {
      setIsDemoMode(isDemo);
      setShowDemoTooltip(true); // Reset tooltip visibility when demo mode changes
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    return () => {
      if (copyToastTimeoutRef.current) {
        window.clearTimeout(copyToastTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadCustomCategories = async () => {
      let localCategories: string[] = [];
      const saved = localStorage.getItem("customTransactionCategories");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            localCategories = parsed;
          }
        } catch (error) {
          console.error("Error loading custom categories:", error);
        }
      }

      if (demoService.isDemo()) {
        if (localCategories.length > 0) {
          setCustomGeneralCategories(localCategories);
          console.log("[DEBUG] Loaded custom categories (demo)", {
            count: localCategories.length,
          });
        }
        return;
      }

      const settings = await settingsService.getUserSettings();
      const settingsCategories = Array.isArray(
        settings.custom_transaction_categories
      )
        ? settings.custom_transaction_categories
        : [];
      const merged = Array.from(
        new Set([...settingsCategories, ...localCategories])
      ).sort((a, b) => a.localeCompare(b));

      setCustomGeneralCategories(merged);
      console.log("[DEBUG] Loaded custom categories (supabase)", {
        count: merged.length,
      });

      if (localCategories.length > 0) {
        const result = await settingsService.saveUserSettings({
          custom_transaction_categories: merged,
        });
        if (result.success) {
          localStorage.removeItem("customTransactionCategories");
          console.log(
            "[DEBUG] Migrated custom categories to Supabase and cleared localStorage",
            {
              count: merged.length,
            }
          );
        } else {
          console.error(
            "[DEBUG] Failed to migrate custom categories to Supabase",
            result.error
          );
        }
      }
    };

    void loadCustomCategories();
  }, []);

  // Migration on first load
  useEffect(() => {
    const migrateData = async () => {
      const migrated = await transactionCategoriesDB.migrateFromLocalStorage();
      if (migrated > 0) {
        console.log(
          `Migrated ${migrated} categories from localStorage to Supabase`
        );
      }
    };
    migrateData();
  }, []);

  const applyIncomeOverrides = async (
    incoming: TransactionWithCategories[],
    persist: boolean
  ) => {
    const updates: TransactionCategory[] = [];
    const creditSamples: Array<{
      id: string;
      amount: number;
      general?: string;
      federal?: string;
      oregon?: string;
    }> = [];
    const updated = incoming.map((transaction) => {
      const descriptionText = `${transaction.merchant_name || ""} ${
        transaction.name || ""
      }`.toLowerCase();
      const isOnlineTransfer =
        descriptionText.includes("online transfer to") ||
        descriptionText.includes("online transfer from") ||
        descriptionText.includes("online trasnfer to") ||
        descriptionText.includes("money transfer from paypal") ||
        descriptionText.includes("deposit digital banking transfer from") ||
        descriptionText.includes("deposit digital banking transfer to");
      const isUserOverride = transaction.ai_confidence === "user_verified";
      if (isOnlineTransfer) {
        if (isUserOverride) {
          return transaction;
        }
        const needsUpdate =
          transaction.general_category !== TRANSFER_GENERAL_CATEGORY ||
          transaction.federal_category !== TRANSFER_TAX_CATEGORY ||
          transaction.oregon_category !== TRANSFER_TAX_CATEGORY;
        if (needsUpdate && persist) {
          updates.push({
            transaction_id: transaction.transaction_id,
            general_category: TRANSFER_GENERAL_CATEGORY,
            federal_category: TRANSFER_TAX_CATEGORY,
            oregon_category: TRANSFER_TAX_CATEGORY,
            ai_processed: true,
            ai_confidence: "high",
            ai_reasoning: "Auto-set transfer for Online Transfer description",
            needs_user_input: false,
          });
        }
        return {
          ...transaction,
          general_category: TRANSFER_GENERAL_CATEGORY,
          federal_category: TRANSFER_TAX_CATEGORY,
          oregon_category: TRANSFER_TAX_CATEGORY,
          ai_confidence: transaction.ai_confidence || "high",
          needs_user_input: false,
        };
      }
      if (isDebitAmount(transaction.amount)) {
        const hasIncome =
          transaction.general_category === INCOME_GENERAL_CATEGORY ||
          transaction.federal_category === INCOME_TAX_CATEGORY ||
          transaction.oregon_category === INCOME_TAX_CATEGORY;
        if (hasIncome && persist) {
          updates.push({
            transaction_id: transaction.transaction_id,
            general_category:
              transaction.general_category === INCOME_GENERAL_CATEGORY
                ? null
                : transaction.general_category || null,
            federal_category:
              transaction.federal_category === INCOME_TAX_CATEGORY
                ? null
                : transaction.federal_category || null,
            oregon_category:
              transaction.oregon_category === INCOME_TAX_CATEGORY
                ? null
                : transaction.oregon_category || null,
            ai_processed: true,
            ai_confidence: "high",
            ai_reasoning: "Cleared income categories for debits",
            needs_user_input: false,
          });
        }
        return {
          ...transaction,
          general_category:
            transaction.general_category === INCOME_GENERAL_CATEGORY
              ? undefined
              : transaction.general_category,
          federal_category:
            transaction.federal_category === INCOME_TAX_CATEGORY
              ? undefined
              : transaction.federal_category,
          oregon_category:
            transaction.oregon_category === INCOME_TAX_CATEGORY
              ? undefined
              : transaction.oregon_category,
        };
      }
      if (isCreditAmount(transaction.amount)) {
        const needsUpdate =
          transaction.general_category !== INCOME_GENERAL_CATEGORY ||
          transaction.federal_category !== INCOME_TAX_CATEGORY ||
          transaction.oregon_category !== INCOME_TAX_CATEGORY;
        if (creditSamples.length < 5) {
          creditSamples.push({
            id: transaction.transaction_id,
            amount: transaction.amount,
            general: transaction.general_category,
            federal: transaction.federal_category,
            oregon: transaction.oregon_category,
          });
        }
        if (needsUpdate && persist) {
          updates.push({
            transaction_id: transaction.transaction_id,
            general_category: INCOME_GENERAL_CATEGORY,
            federal_category: INCOME_TAX_CATEGORY,
            oregon_category: INCOME_TAX_CATEGORY,
            ai_processed: true,
            ai_confidence: "high",
            ai_reasoning: "Auto-set income for credits (amount < 0)",
            needs_user_input: false,
          });
        }
        return {
          ...transaction,
          general_category: INCOME_GENERAL_CATEGORY,
          federal_category: INCOME_TAX_CATEGORY,
          oregon_category: INCOME_TAX_CATEGORY,
          ai_confidence: transaction.ai_confidence || "high",
          needs_user_input: false,
        };
      }
      return transaction;
    });

    if (persist && updates.length > 0) {
      const success = await transactionCategoriesDB.upsertCategories(updates);
      console.log("[DEBUG] Applied income overrides:", {
        updates: updates.length,
        success,
        creditSamples,
      });
    } else {
      console.log("[DEBUG] Income override check:", {
        creditsChecked: creditSamples.length,
        updates: updates.length,
        persist,
        creditSamples,
      });
    }

    return updated;
  };

  const handleSort = (
    key:
      | "date"
      | "amount"
      | "description"
      | "general_category"
      | "federal_category"
      | "oregon_category"
  ) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          ...prev,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      const defaultDirection =
        key === "description" ||
        key === "general_category" ||
        key === "federal_category" ||
        key === "oregon_category"
          ? "asc"
          : "desc";
      return { key, direction: defaultDirection };
    });
  };

  const handleCopyTransactionId = async (transactionId: string) => {
    console.log("[DEBUG] Copying transaction ID:", {
      transactionId,
    });
    try {
      await navigator.clipboard.writeText(transactionId);
      console.log("[DEBUG] Copied transaction ID to clipboard:", {
        transactionId,
      });
      setCopyToastOpen(true);
      if (copyToastTimeoutRef.current) {
        window.clearTimeout(copyToastTimeoutRef.current);
      }
      copyToastTimeoutRef.current = window.setTimeout(() => {
        setCopyToastOpen(false);
      }, 1600);
    } catch (error) {
      console.error("Error copying transaction ID:", error);
    }
  };

  // Load transactions
  const loadTransactions = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      const requestKey = `${startDate}|${endDate}|${demoService.isDemo()}`;
      console.log("[DEBUG] loadTransactions called", {
        requestKey,
        currentLoadKey: loadKeyRef.current,
        loadInFlight: loadInFlightRef.current,
        silent,
      });
      if (loadInFlightRef.current && loadKeyRef.current === requestKey) {
        console.debug("[DEBUG] Skipping duplicate transactions load", {
          requestKey,
        });
        return;
      }
      loadInFlightRef.current = true;
      loadKeyRef.current = requestKey;
      try {
        if (!silent) {
          setLoading(true);
        }
        setError(null);

        // Check if demo mode is active
        if (demoService.isDemo()) {
          // Load demo data
          const demoData = demoService.getAllDemoData();

          // Format demo accounts
          const demoAccounts = demoData.accounts.map((account: any) => ({
            ...account,
            account_id: account.id,
            balances: {
              available: account.available_balance || account.balance,
              current: account.balance,
              iso_currency_code: "USD",
              limit: account.credit_limit || null,
              unofficial_currency_code: null,
            },
            official_name: account.name,
            persistent_account_id: account.id,
          }));

          setAccounts(demoAccounts);

          // Format demo transactions
          const demoTransactions = demoData.transactions.map(
            (transaction: any) => ({
              ...transaction,
              transaction_id: transaction.id,
              account_owner: null,
              authorized_date: null,
              authorized_datetime: null,
              datetime: null,
              location: {
                address: null,
                city: null,
                country: null,
                lat: null,
                lon: null,
                postal_code: null,
                region: null,
                store_number: null,
              },
              payment_meta: {
                by_order_of: null,
                payee: null,
                payer: null,
                payment_method: transaction.payment_method || null,
                payment_processor: null,
                ppd_id: null,
                reason: null,
                reference_number: null,
              },
              personal_finance_category: null,
              transaction_code: null,
              iso_currency_code: "USD",
              unofficial_currency_code: null,
              internal_category: transaction.category || [],
              check_number: null,
              transaction_type: transaction.amount > 0 ? "debit" : "credit",
            })
          );

          // Load categories from Supabase
          const transactionIds = demoTransactions.map(
            (t: any) => t.transaction_id
          );
          console.log("[DEBUG] Loading categories for demo transactions:", {
            transactionCount: transactionIds.length,
            sampleIds: transactionIds.slice(0, 3),
            isDemoMode: true,
          });
          const categories =
            await transactionCategoriesDB.getCategoriesForTransactions(
              transactionIds
            );
          console.log("[DEBUG] Loaded categories from Supabase:", {
            categoriesFound: categories.length,
            transactionsRequested: transactionIds.length,
            matchRate: `${(
              (categories.length / transactionIds.length) *
              100
            ).toFixed(1)}%`,
            sampleCategories: categories.slice(0, 3),
          });

          // Merge categories with transactions
          const categoriesMap = new Map(
            categories.map((c) => [c.transaction_id, c])
          );
          const transactionsWithCategories = demoTransactions.map((t: any) => {
            const cat = categoriesMap.get(t.transaction_id);
            if (cat) {
              console.log("[DEBUG] Merging category for transaction:", {
                transaction_id: t.transaction_id,
                merchant: t.merchant_name || t.name,
                category: cat.general_category,
              });
            }
            return {
              ...t,
              general_category: cat?.general_category ?? undefined,
              category_icon:
                cat?.category_icon ??
                getTransactionCategoryIconKey(cat?.general_category) ??
                undefined,
              federal_category: cat?.federal_category ?? undefined,
              oregon_category: cat?.oregon_category ?? undefined,
              custom_description: cat?.custom_description ?? undefined,
              ai_confidence: cat?.ai_confidence,
              needs_user_input: cat?.needs_user_input,
            };
          });
          const withIncomeOverrides = await applyIncomeOverrides(
            transactionsWithCategories,
            !demoService.isDemo()
          );
          const categorizedCount = withIncomeOverrides.filter(
            (t) => t.general_category
          ).length;
          console.log("[DEBUG] Final merged transactions:", {
            totalTransactions: transactionsWithCategories.length,
            withCategories: categorizedCount,
            sample: transactionsWithCategories.slice(0, 3).map((t) => ({
              id: t.transaction_id,
              name: t.name,
              category: t.general_category,
            })),
          });
          // Show a temporary status message if categories were loaded
          if (categorizedCount > 0) {
            setAiStatus(
              `Loaded ${categorizedCount} saved categories from database`
            );
            setTimeout(() => setAiStatus(""), 3000);
          }
          setTransactions(
            withIncomeOverrides.map((t) => ({
              ...t,
              general_category: t.general_category ?? undefined,
              federal_category: t.federal_category ?? undefined,
              oregon_category: t.oregon_category ?? undefined,
            }))
          );
          setCacheStatus({ lastSyncedAt: null, refreshing: false });
        } else {
          // Load real transactions from Plaid using the date range state
          const transactionsStart = performance.now();
          const { transactions: transactionsData, cacheStatus } =
            await plaidService.getTransactionsWithCacheStatus(
              startDate,
              endDate
            );
          console.log("[DEBUG] Transactions fetch timing", {
            ms: Math.round(performance.now() - transactionsStart),
            count: transactionsData?.length ?? 0,
          });

          setCacheStatus(cacheStatus);

          void (async () => {
            const accountsStart = performance.now();
            try {
              let resolvedAccounts = await plaidService.getAccountsCached();
              if (resolvedAccounts.length === 0) {
                console.log("[DEBUG] Accounts cache empty, refreshing now");
                resolvedAccounts = await plaidService.refreshAccountsCache();
                console.log("[DEBUG] Accounts cache refreshed", {
                  count: resolvedAccounts.length,
                });
              }
              setAccounts(resolvedAccounts);
            } catch (error) {
              console.error("[DEBUG] Accounts cache load failed", error);
            } finally {
              console.log("[DEBUG] Accounts fetch timing", {
                ms: Math.round(performance.now() - accountsStart),
              });
            }
          })();

          void (async () => {
            const namesStart = performance.now();
            try {
              const customNamesData = await plaidService.getCustomNames();
              setCustomNames(customNamesData || {});
            } catch (error) {
              console.error("[DEBUG] Custom names load failed", error);
            } finally {
              console.log("[DEBUG] Custom names timing", {
                ms: Math.round(performance.now() - namesStart),
              });
            }
          })();
          if (
            !demoService.isDemo() &&
            autoRefreshKeyRef.current !== requestKey
          ) {
            autoRefreshKeyRef.current = requestKey;
            void (async () => {
              try {
                const refreshResult =
                  await plaidService.refreshTransactionsCache();
                console.log("[DEBUG] Auto refresh result", refreshResult);
                if (refreshResult.status === "refreshing") {
                  setCacheStatus((prev) => ({
                    ...prev,
                    refreshing: true,
                  }));
                }
              } catch (refreshError) {
                console.error("[DEBUG] Auto refresh failed", refreshError);
              }
            })();
          }

          const baseTransactions = (transactionsData || []).map((t) => ({
            ...t,
            general_category: isCreditAmount(t.amount)
              ? INCOME_GENERAL_CATEGORY
              : undefined,
            federal_category: isCreditAmount(t.amount)
              ? INCOME_TAX_CATEGORY
              : undefined,
            oregon_category: isCreditAmount(t.amount)
              ? INCOME_TAX_CATEGORY
              : undefined,
          }));
          const creditCount = baseTransactions.filter((t) =>
            isCreditAmount(t.amount)
          ).length;
          console.log("[DEBUG] Base transactions income preset:", {
            total: baseTransactions.length,
            credits: creditCount,
          });
          setTransactions(baseTransactions);

          // Load categories from Supabase without blocking initial render
          const transactionIds = (transactionsData || []).map(
            (t) => t.transaction_id
          );
          setCategoriesLoading(true);
          void (async () => {
            try {
              console.log("[DEBUG] Loading categories for real transactions:", {
                transactionCount: transactionIds.length,
                sampleIds: transactionIds.slice(0, 3),
                isDemoMode: false,
              });
              const categories =
                await transactionCategoriesDB.getCategoriesForTransactions(
                  transactionIds
                );
              console.log("[DEBUG] Loaded categories from Supabase (real):", {
                categoriesFound: categories.length,
                transactionsRequested: transactionIds.length,
                matchRate: `${(
                  (categories.length / transactionIds.length) *
                  100
                ).toFixed(1)}%`,
              });

              if (loadKeyRef.current !== requestKey) {
                console.log(
                  "[DEBUG] Skipping category merge for stale transactions load",
                  {
                    requestKey,
                    currentLoadKey: loadKeyRef.current,
                    categoriesFound: categories.length,
                  }
                );
                return;
              }
              console.log("[DEBUG] Proceeding with category merge", {
                requestKey,
                currentLoadKey: loadKeyRef.current,
                categoriesFound: categories.length,
              });

              // Merge categories with transactions
              const categoriesMap = new Map(
                categories.map((c) => [c.transaction_id, c])
              );
              const transactionsWithCategories = (transactionsData || []).map(
                (t) => {
                  const cat = categoriesMap.get(t.transaction_id);
                  return {
                    ...t,
                    general_category: cat?.general_category ?? undefined,
                    category_icon:
                      cat?.category_icon ??
                      getTransactionCategoryIconKey(cat?.general_category) ??
                      undefined,
                    federal_category: cat?.federal_category ?? undefined,
                    oregon_category: cat?.oregon_category ?? undefined,
                    custom_description: cat?.custom_description ?? undefined,
                    ai_confidence: cat?.ai_confidence,
                    needs_user_input: cat?.needs_user_input,
                  };
                }
              );
              const withIncomeOverrides = await applyIncomeOverrides(
                transactionsWithCategories,
                !demoService.isDemo()
              );

              setTransactions(
                withIncomeOverrides.map((t) => ({
                  ...t,
                  general_category: t.general_category ?? undefined,
                  federal_category: t.federal_category ?? undefined,
                  oregon_category: t.oregon_category ?? undefined,
                }))
              );
            } catch (categoryError) {
              console.error(
                "[DEBUG] Failed to load transaction categories:",
                categoryError
              );
            } finally {
              if (loadKeyRef.current === requestKey) {
                setCategoriesLoading(false);
              }
            }
          })();
        }

        // Load learned patterns in background
        void (async () => {
          const patternsStart = performance.now();
          try {
            const patterns = await transactionCategoriesDB.getLearnedPatterns();
            if (loadKeyRef.current === requestKey) {
              setLearnedPatterns(patterns);
            }
          } catch (patternError) {
            console.error(
              "[DEBUG] Failed to load learned patterns:",
              patternError
            );
          } finally {
            console.log("[DEBUG] Learned patterns timing", {
              ms: Math.round(performance.now() - patternsStart),
            });
          }
        })();
      } catch (error: any) {
        console.error("Error loading transactions:", error);
        setError(error.message || "Failed to load transactions");
        setCategoriesLoading(false);
        setCacheStatus({
          lastSyncedAt: null,
          refreshing: false,
          cacheReadFailed: true,
        });
      } finally {
        if (loadKeyRef.current === requestKey) {
          loadInFlightRef.current = false;
        }
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [startDate, endDate]
  );

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  useEffect(() => {
    console.log("[DEBUG] Transactions cache status", {
      lastSyncedAt: cacheStatus.lastSyncedAt,
      refreshing: cacheStatus.refreshing,
      cacheReadFailed: cacheStatus.cacheReadFailed,
    });
  }, [
    cacheStatus.lastSyncedAt,
    cacheStatus.refreshing,
    cacheStatus.cacheReadFailed,
  ]);

  useEffect(() => {
    const customCount = transactions.filter((t) => t.custom_description).length;
    const withOriginal = transactions.filter(
      (t) => t.custom_description && t.original_description
    ).length;
    if (customCount > 0) {
      console.log("[DEBUG] Custom descriptions summary", {
        customCount,
        withOriginal,
      });
    }
  }, [transactions]);
  useEffect(() => {
    if (!cacheStatus.refreshing || demoService.isDemo()) {
      cachePollAttemptsRef.current = 0;
      return;
    }

    const interval = window.setInterval(async () => {
      cachePollAttemptsRef.current += 1;
      if (cachePollAttemptsRef.current > 10) {
        console.log("[DEBUG] Cache status polling stopped", {
          attempts: cachePollAttemptsRef.current,
        });
        window.clearInterval(interval);
        return;
      }
      try {
        const status = await plaidService.getTransactionsCacheStatus();
        console.log("[DEBUG] Cache status poll", status);
        if (!status.refreshing) {
          window.clearInterval(interval);
          await loadTransactions({ silent: true });
        }
      } catch (error) {
        console.error("[DEBUG] Cache status polling failed", error);
      }
    }, 15000);

    return () => window.clearInterval(interval);
  }, [cacheStatus.refreshing, loadTransactions]);

  const handleSyncTransactions = useCallback(async () => {
    if (syncing) {
      return;
    }
    setSyncing(true);
    console.log("[DEBUG] Transactions cache sync started", {
      startDate,
      endDate,
    });
    try {
      const result = await plaidService.refreshTransactionsCache({
        force: true,
        wait: true,
      });
      console.log("[DEBUG] Transactions cache sync finished", result);
      await loadTransactions({ silent: true });
    } catch (error) {
      console.error("[DEBUG] Transactions cache sync failed", error);
      setError("Failed to sync transactions");
    } finally {
      setSyncing(false);
    }
  }, [endDate, loadTransactions, startDate, syncing]);

  const handleShareTransaction = async (
    transaction: TransactionWithCategories
  ) => {
    setShareTransaction(transaction);
    setShareOpen(true);

    if (groups.length > 0) {
      return;
    }

    try {
      const availableGroups = await groupAPI.list();
      setGroups(availableGroups);
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  };

  const handleShareRecord = (
    groupIds: string[],
    permission: "view" | "edit"
  ) => {
    if (!shareTransaction) {
      return;
    }

    void (async () => {
      try {
        await shareAPI.shareRecord({
          recordType: "transaction",
          recordId: shareTransaction.transaction_id,
          groupIds,
          permission,
        });
        setTransactions((prev) =>
          prev.map((transaction) =>
            transaction.transaction_id === shareTransaction.transaction_id
              ? { ...transaction, is_shared: true, isShared: true }
              : transaction
          )
        );
      } catch (error) {
        console.error("Failed to share transaction:", error);
        alert("Failed to share transaction. Please try again.");
      }
    })();
  };

  // Smart Categorize All - the one button to rule them all
  const handleSmartCategorizeAll = () => {
    if (transactions.length === 0) {
      setError("No transactions to categorize");
      return;
    }

    setError(null);
    setAiProcessing(false);
    setAiProgress(0);
    const uncategorized = transactions.filter(
      (transaction) =>
        !transaction.general_category ||
        String(transaction.general_category).trim() === ""
    );
    console.log("[HelpMeLearn] Opening from AI Categorize button", {
      total: transactions.length,
      uncategorized: uncategorized.length,
    });
    if (uncategorized.length === 0) {
      setAiStatus("No uncategorized transactions found.");
      return;
    }
    setTeachingQueue(uncategorized);
    setCurrentTeachingIndex(0);
    setShowTeachingModal(true);
    setAiStatus(`Ready to categorize ${uncategorized.length} transactions.`);
  };

  const saveCustomGeneralCategories = async (categories: string[]) => {
    setCustomGeneralCategories(categories);
    localStorage.setItem(
      "customTransactionCategories",
      JSON.stringify(categories)
    );
    console.log("[DEBUG] Updated custom categories locally", {
      count: categories.length,
    });
    if (demoService.isDemo()) {
      return;
    }

    const result = await settingsService.saveUserSettings({
      custom_transaction_categories: categories,
    });
    if (result.success) {
      localStorage.removeItem("customTransactionCategories");
      console.log("[DEBUG] Saved custom categories to Supabase", {
        count: categories.length,
      });
    } else {
      console.error(
        "[DEBUG] Failed to save custom categories to Supabase",
        result.error
      );
    }
  };

  // Handle user teaching
  const handleUserCategorization = async (
    transactionId: string,
    generalCategory: string,
    federalCategory: string | null,
    oregonCategory: string | null,
    applyToSimilar: boolean
  ) => {
    const transaction = teachingQueue[currentTeachingIndex];
    if (
      transaction &&
      isDebitAmount(transaction.amount) &&
      (generalCategory === INCOME_GENERAL_CATEGORY ||
        federalCategory === INCOME_TAX_CATEGORY ||
        oregonCategory === INCOME_TAX_CATEGORY)
    ) {
      setError("Debits cannot be categorized as Income.");
      return;
    }
    const normalizedCategory = generalCategory.trim();
    const hasCustomCategory =
      normalizedCategory.length > 0 &&
      !GENERAL_CATEGORIES.some(
        (cat) => cat.toLowerCase() === normalizedCategory.toLowerCase()
      ) &&
      !customGeneralCategories.some(
        (cat) => cat.toLowerCase() === normalizedCategory.toLowerCase()
      );

    if (hasCustomCategory) {
      const updated = [...customGeneralCategories, normalizedCategory].sort(
        (a, b) => a.localeCompare(b)
      );
      await saveCustomGeneralCategories(updated);
      console.log("[DEBUG] Added custom category", {
        category: normalizedCategory,
        total: updated.length,
      });
    }

    // Save the categorization
    const category: TransactionCategory = {
      transaction_id: transactionId,
      general_category: normalizedCategory,
      category_icon:
        getTransactionCategoryIconKey(normalizedCategory) ?? undefined,
      federal_category: federalCategory,
      oregon_category: oregonCategory,
      ai_processed: true,
      ai_confidence: "user_verified",
      ai_reasoning: "User provided categorization",
      needs_user_input: false,
    };

    await transactionCategoriesDB.upsertCategories([category]);

    // Save as learned pattern
    if (transaction) {
      await transactionCategoriesDB.saveLearnedPattern({
        merchant_pattern: transaction.merchant_name,
        transaction_pattern: transaction.name,
        general_category: generalCategory,
        federal_category: federalCategory ?? undefined,
        oregon_category: oregonCategory ?? undefined,
        confidence_score: 1.0,
        times_used: 1,
        times_confirmed: 1,
        times_rejected: 0,
      });
    }

    // Collect IDs of transactions that will be categorized
    const categorizedIds = new Set([transactionId]);

    // Apply to similar transactions if requested
    if (applyToSimilar && transaction) {
      const merchantKey = transaction.merchant_name?.trim();
      const nameKey = transaction.name?.trim();
      if (!merchantKey && !nameKey) {
        console.warn(
          "[DEBUG] applyToSimilar skipped: no merchant or name to match",
          { transactionId }
        );
      }
      const similar = transactions.filter((t) => {
        if (t.transaction_id === transactionId) return false;
        const merchantMatch =
          merchantKey && t.merchant_name?.trim() === merchantKey;
        const nameMatch = nameKey && t.name?.trim() === nameKey;
        return Boolean(merchantMatch || nameMatch);
      });
      console.debug("[DEBUG] applyToSimilar matched transactions", {
        transactionId,
        merchantKey,
        nameKey,
        matches: similar.length,
      });

      if (similar.length > 0) {
        const similarCategories = similar.map((t) => {
          categorizedIds.add(t.transaction_id);
          return {
            transaction_id: t.transaction_id,
            general_category: generalCategory,
            category_icon:
              getTransactionCategoryIconKey(generalCategory) ?? undefined,
            federal_category: federalCategory,
            oregon_category: oregonCategory,
            ai_processed: true,
            ai_confidence: "user_verified" as const,
            ai_reasoning: "Applied from similar transaction",
            needs_user_input: false,
          };
        });

        await transactionCategoriesDB.upsertCategories(similarCategories);
      }
    }

    // Update local state
    setTransactions((prev) =>
      prev.map((t) => {
        if (
          t.transaction_id === transactionId ||
          (applyToSimilar &&
            transaction &&
            (t.merchant_name === transaction.merchant_name ||
              t.name === transaction.name))
        ) {
          return {
            ...t,
            general_category: generalCategory,
            category_icon:
              getTransactionCategoryIconKey(generalCategory) ?? undefined,
            federal_category: federalCategory ?? undefined,
            oregon_category: oregonCategory ?? undefined,
            ai_confidence: "user_verified",
            needs_user_input: false,
          };
        }
        return t;
      })
    );

    // Remove categorized transactions from the teaching queue
    const updatedQueue = teachingQueue.filter(
      (t) => !categorizedIds.has(t.transaction_id)
    );
    setTeachingQueue(updatedQueue);

    // Find next uncategorized transaction
    if (updatedQueue.length > 0) {
      // Stay at current index (or go to 0 if we're at/past the end)
      const nextIndex = Math.min(currentTeachingIndex, updatedQueue.length - 1);
      setCurrentTeachingIndex(nextIndex);
    } else {
      // No more transactions - close everything
      closeTeachingModal();
      setAiStatus("✅ Categorization complete with user teaching!");

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setAiStatus("");
      }, 3000);
    }
  };

  // Handle skip in teaching modal
  const handleSkipTeaching = () => {
    if (currentTeachingIndex < teachingQueue.length - 1) {
      setCurrentTeachingIndex((prev) => prev + 1);
    } else {
      // Last transaction - close everything
      closeTeachingModal();
      setAiStatus("✅ Categorization complete (some skipped)");

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setAiStatus("");
      }, 3000);
    }
  };

  // Handle modal close
  const closeTeachingModal = () => {
    setShowTeachingModal(false);
    setTeachingQueue([]);
    setCurrentTeachingIndex(0);
    setAiProcessing(false);
    setAiProgress(0);
  };

  // Clear all categories
  const runClearAllCategories = useCallback(async () => {
    const success = await transactionCategoriesDB.clearAllCategories();
    if (!success) {
      setError("Failed to clear categories");
      return;
    }
    setTransactions((prev) =>
      prev.map((t) => ({
        ...t,
        general_category: undefined,
        federal_category: undefined,
        oregon_category: undefined,
        ai_confidence: undefined,
        needs_user_input: undefined,
      }))
    );
    setAiStatus("All categories cleared");
  }, []);

  const handleClearAllCategories = async () => {
    if (
      window.confirm(
        "Are you sure you want to clear all categories? This cannot be undone."
      )
    ) {
      await runClearAllCategories();
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("clearTransactionCategories") !== "1") return;
    params.delete("clearTransactionCategories");
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${
      nextSearch ? `?${nextSearch}` : ""
    }${window.location.hash}`;
    window.history.replaceState({}, "", nextUrl);
    console.log("[DEBUG] Clear-all categories triggered via URL param");
    void runClearAllCategories();
  }, [runClearAllCategories]);

  const getOriginalDescriptionText = (
    transaction: TransactionWithCategories
  ) => {
    if (!transaction.custom_description) {
      if (
        transaction.merchant_name &&
        transaction.name &&
        transaction.merchant_name !== transaction.name
      ) {
        return transaction.name;
      }
      return "";
    }
    return (
      transaction.original_description ||
      transaction.name ||
      transaction.merchant_name ||
      ""
    );
  };

  const handleStartDescriptionEdit = (
    transaction: TransactionWithCategories
  ) => {
    const base = transaction.merchant_name || transaction.name || "";
    setEditingDescriptionId(transaction.transaction_id);
    setEditingDescriptionValue(transaction.custom_description || base);
    console.log("[DEBUG] Start description edit", {
      transactionId: transaction.transaction_id,
      base,
      custom: transaction.custom_description,
    });
  };

  const handleCancelDescriptionEdit = () => {
    console.log("[DEBUG] Cancel description edit");
    setEditingDescriptionId(null);
    setEditingDescriptionValue("");
  };

  const handleSaveDescription = async (
    transaction: TransactionWithCategories
  ) => {
    const base = transaction.merchant_name || transaction.name || "";
    const trimmed = editingDescriptionValue.trim();
    const currentCustom = transaction.custom_description || "";
    const nextCustom = trimmed && trimmed !== base ? trimmed : "";
    if (trimmed === base || trimmed === currentCustom) {
      handleCancelDescriptionEdit();
      return;
    }
    console.log("[DEBUG] Saving custom description", {
      transactionId: transaction.transaction_id,
      nextCustom,
    });
    try {
      await transactionCategoriesDB.upsertCategories([
        {
          transaction_id: transaction.transaction_id,
          general_category: transaction.general_category ?? null,
          federal_category: transaction.federal_category ?? null,
          oregon_category: transaction.oregon_category ?? null,
          custom_description: nextCustom || null,
          ai_processed: true,
          ai_confidence: "user_verified",
          ai_reasoning: "User updated description",
          needs_user_input: false,
        },
      ]);
      setTransactions((prev) =>
        prev.map((t) =>
          t.transaction_id === transaction.transaction_id
            ? {
                ...t,
                custom_description: nextCustom || undefined,
                ai_confidence: "user_verified",
                needs_user_input: false,
              }
            : t
        )
      );
    } catch (error) {
      console.error("[DEBUG] Failed to save description", error);
      setError("Failed to save description");
    } finally {
      handleCancelDescriptionEdit();
    }
  };

  // Load organizations + account→org mapping (for the Organization filter).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [orgs, accOrgs] = await Promise.all([
          organizationService.getAll(),
          organizationService.getAccountOrganizations(),
        ]);
        if (cancelled) return;
        setOrganizations(orgs);
        setAccountOrganizations(accOrgs);
      } catch (err) {
        console.error("[TransactionsPage] Failed to load organizations", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist filter profiles.
  useEffect(() => {
    try {
      localStorage.setItem(FILTER_PROFILES_KEY, JSON.stringify(filterProfiles));
    } catch {
      // ignore storage failures
    }
  }, [filterProfiles]);

  // Per-account lookups for filtering.
  const accountById = useMemo(() => {
    const map: Record<string, PlaidAccount> = {};
    accounts.forEach((a) => {
      map[a.account_id] = a;
    });
    return map;
  }, [accounts]);

  // Distinct institution names and account types present in the user's accounts.
  const institutionOptions = useMemo(
    () =>
      Array.from(
        new Set(accounts.map((a) => resolveInstitutionName(a)).filter(Boolean))
      ).sort(),
    [accounts]
  );
  const accountTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          accounts.map((a) => a.subtype || a.type).filter(Boolean) as string[]
        )
      ).sort(),
    [accounts]
  );

  // ----- Filter profiles -----
  const currentFilterState = useCallback(
    (): TxnFilterProfileFilters => ({
      searchTerm,
      selectedAccount,
      selectedOrganization,
      selectedInstitution,
      selectedAccountType,
      amountMin,
      amountMax,
      filterGeneralCategory,
      filterFederalCategory,
      filterOregonCategory,
      sharedOnly,
    }),
    [
      searchTerm,
      selectedAccount,
      selectedOrganization,
      selectedInstitution,
      selectedAccountType,
      amountMin,
      amountMax,
      filterGeneralCategory,
      filterFederalCategory,
      filterOregonCategory,
      sharedOnly,
    ]
  );

  const applyFilterProfile = useCallback((profile: TxnFilterProfile) => {
    console.log("[DEBUG] Applying filter profile", {
      id: profile.id,
      name: profile.name,
      filters: profile.filters,
    });
    const f = profile.filters;
    setSearchTerm(f.searchTerm || "");
    setSelectedAccount(f.selectedAccount || "");
    setSelectedOrganization(f.selectedOrganization || "");
    setSelectedInstitution(f.selectedInstitution || "");
    setSelectedAccountType(f.selectedAccountType || "");
    setAmountMin(f.amountMin || "");
    setAmountMax(f.amountMax || "");
    setFilterGeneralCategory(f.filterGeneralCategory || "");
    setFilterFederalCategory(f.filterFederalCategory || "");
    setFilterOregonCategory(f.filterOregonCategory || "");
    setSharedOnly(Boolean(f.sharedOnly));
    setActiveProfileId(profile.id);
    setShowAdvancedFilters(true);
  }, []);

  const saveFilterProfile = useCallback(() => {
    const name = window.prompt("Name this filter profile:");
    if (!name || !name.trim()) {
      console.log("[DEBUG] Save filter profile cancelled (no name)");
      return;
    }
    const profile: TxnFilterProfile = {
      id: `prof-${name.trim().toLowerCase().replace(/\s+/g, "-")}-${
        filterProfiles.length
      }`,
      name: name.trim(),
      filters: currentFilterState(),
    };
    console.log("[DEBUG] Saving filter profile", {
      id: profile.id,
      name: profile.name,
      filters: profile.filters,
    });
    setFilterProfiles((prev) => [...prev, profile]);
    setActiveProfileId(profile.id);
  }, [currentFilterState, filterProfiles.length]);

  const deleteActiveProfile = useCallback(() => {
    if (!activeProfileId) return;
    console.log("[DEBUG] Deleting filter profile", { id: activeProfileId });
    setFilterProfiles((prev) => prev.filter((p) => p.id !== activeProfileId));
    setActiveProfileId("");
  }, [activeProfileId]);

  const hasActiveAdvancedFilters =
    selectedOrganization !== "" ||
    selectedInstitution !== "" ||
    selectedAccountType !== "" ||
    amountMin !== "" ||
    amountMax !== "" ||
    filterGeneralCategory !== "" ||
    filterFederalCategory !== "" ||
    filterOregonCategory !== "";

  // Log whenever any filter selection changes so we can debug filtering live.
  useEffect(() => {
    console.log("[DEBUG] Transaction filters changed", {
      searchTerm,
      selectedAccount,
      selectedOrganization,
      selectedInstitution,
      selectedAccountType,
      amountMin,
      amountMax,
      filterGeneralCategory,
      filterFederalCategory,
      filterOregonCategory,
      sharedOnly,
    });
  }, [
    searchTerm,
    selectedAccount,
    selectedOrganization,
    selectedInstitution,
    selectedAccountType,
    amountMin,
    amountMax,
    filterGeneralCategory,
    filterFederalCategory,
    filterOregonCategory,
    sharedOnly,
  ]);

  const clearAdvancedFilters = useCallback(() => {
    console.log("[DEBUG] Clearing advanced transaction filters");
    setSelectedOrganization("");
    setSelectedInstitution("");
    setSelectedAccountType("");
    setAmountMin("");
    setAmountMax("");
    setFilterGeneralCategory("");
    setFilterFederalCategory("");
    setFilterOregonCategory("");
    setActiveProfileId("");
  }, []);

  // Filter transactions
  const filteredTransactions = transactions
    .filter((t) => {
      const isShared = t.is_shared || t.isShared;
      const matchesSearch =
        searchTerm === "" ||
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.merchant_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesAccount =
        selectedAccount === "" || t.account_id === selectedAccount;

      const matchesDateRange = t.date >= startDate && t.date <= endDate;

      const matchesShared = !sharedOnly || isShared;

      const acct = accountById[t.account_id];
      const matchesOrganization =
        selectedOrganization === "" ||
        (acct &&
          accountOrganizations[acct.account_id] === selectedOrganization);
      const matchesInstitution =
        selectedInstitution === "" ||
        (acct && resolveInstitutionName(acct) === selectedInstitution);
      const matchesAccountType =
        selectedAccountType === "" ||
        (acct && (acct.subtype || acct.type) === selectedAccountType);

      const min = amountMin === "" ? null : Number(amountMin);
      const max = amountMax === "" ? null : Number(amountMax);
      const absAmount = Math.abs(t.amount);
      const matchesAmount =
        (min === null || Number.isNaN(min) || absAmount >= min) &&
        (max === null || Number.isNaN(max) || absAmount <= max);

      const matchesGeneral =
        filterGeneralCategory === "" ||
        (t.general_category || "") === filterGeneralCategory;
      const matchesFederal =
        filterFederalCategory === "" ||
        resolveTaxName(t.federal_category, US_FEDERAL_TAX_CATEGORIES) ===
          filterFederalCategory;
      const matchesOregon =
        filterOregonCategory === "" ||
        resolveTaxName(t.oregon_category, OREGON_STATE_TAX_CATEGORIES) ===
          filterOregonCategory;

      return (
        matchesSearch &&
        matchesAccount &&
        matchesDateRange &&
        matchesShared &&
        matchesOrganization &&
        matchesInstitution &&
        matchesAccountType &&
        matchesAmount &&
        matchesGeneral &&
        matchesFederal &&
        matchesOregon
      );
    })
    .sort((a, b) => {
      const direction = sortConfig.direction === "asc" ? 1 : -1;
      switch (sortConfig.key) {
        case "amount":
          return (a.amount - b.amount) * direction;
        case "description":
          return a.name.localeCompare(b.name) * direction;
        case "general_category": {
          const left = formatDisplayLabel(
            a.general_category || ""
          ).toLowerCase();
          const right = formatDisplayLabel(
            b.general_category || ""
          ).toLowerCase();
          return left.localeCompare(right) * direction;
        }
        case "federal_category": {
          const left = formatDisplayLabel(
            a.federal_category || ""
          ).toLowerCase();
          const right = formatDisplayLabel(
            b.federal_category || ""
          ).toLowerCase();
          return left.localeCompare(right) * direction;
        }
        case "oregon_category": {
          const left = formatDisplayLabel(
            a.oregon_category || ""
          ).toLowerCase();
          const right = formatDisplayLabel(
            b.oregon_category || ""
          ).toLowerCase();
          return left.localeCompare(right) * direction;
        }
        case "date":
        default: {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return (dateA - dateB) * direction;
        }
      }
    });

  const totalDeposits = useMemo(() => {
    const normalizeCategoryValue = (value?: string | null) =>
      String(value || "")
        .trim()
        .toLowerCase();
    const isTransferCategory = (transaction: TransactionWithCategories) => {
      const general = normalizeCategoryValue(transaction.general_category);
      const federal = normalizeCategoryValue(transaction.federal_category);
      const oregon = normalizeCategoryValue(transaction.oregon_category);
      return (
        general === TRANSFER_GENERAL_CATEGORY.toLowerCase() ||
        federal === TRANSFER_TAX_CATEGORY ||
        oregon === TRANSFER_TAX_CATEGORY
      );
    };
    const isOnlineTransferDescription = (
      transaction: TransactionWithCategories
    ) => {
      const descriptionText = `${transaction.merchant_name || ""} ${
        transaction.name || ""
      }`.toLowerCase();
      return (
        descriptionText.includes("online transfer to") ||
        descriptionText.includes("online transfer from") ||
        descriptionText.includes("online trasnfer to") ||
        descriptionText.includes("money transfer from paypal") ||
        descriptionText.includes("deposit digital banking transfer from") ||
        descriptionText.includes("deposit digital banking transfer to")
      );
    };
    const isTransferTransaction = (transaction: TransactionWithCategories) => {
      const isUserOverride = transaction.ai_confidence === "user_verified";
      if (isUserOverride) {
        return isTransferCategory(transaction);
      }
      return (
        isTransferCategory(transaction) ||
        isOnlineTransferDescription(transaction)
      );
    };
    const deposits = filteredTransactions.filter((t) => t.amount < 0);
    const eligible = includeTransfers
      ? deposits
      : deposits.filter((t) => !isTransferTransaction(t));
    return {
      total: eligible.reduce((sum, t) => sum + Math.abs(t.amount), 0),
      count: eligible.length,
    };
  }, [filteredTransactions, includeTransfers]);

  const totalDebits = useMemo(() => {
    const normalizeCategoryValue = (value?: string | null) =>
      String(value || "")
        .trim()
        .toLowerCase();
    const isTransferCategory = (transaction: TransactionWithCategories) => {
      const general = normalizeCategoryValue(transaction.general_category);
      const federal = normalizeCategoryValue(transaction.federal_category);
      const oregon = normalizeCategoryValue(transaction.oregon_category);
      return (
        general === TRANSFER_GENERAL_CATEGORY.toLowerCase() ||
        federal === TRANSFER_TAX_CATEGORY ||
        oregon === TRANSFER_TAX_CATEGORY
      );
    };
    const isOnlineTransferDescription = (
      transaction: TransactionWithCategories
    ) => {
      const descriptionText = `${transaction.merchant_name || ""} ${
        transaction.name || ""
      }`.toLowerCase();
      return (
        descriptionText.includes("online transfer to") ||
        descriptionText.includes("online transfer from") ||
        descriptionText.includes("online trasnfer to") ||
        descriptionText.includes("money transfer from paypal") ||
        descriptionText.includes("deposit digital banking transfer from") ||
        descriptionText.includes("deposit digital banking transfer to")
      );
    };
    const isTransferTransaction = (transaction: TransactionWithCategories) => {
      const isUserOverride = transaction.ai_confidence === "user_verified";
      if (isUserOverride) {
        return isTransferCategory(transaction);
      }
      return (
        isTransferCategory(transaction) ||
        isOnlineTransferDescription(transaction)
      );
    };
    const debits = filteredTransactions.filter((t) => t.amount > 0);
    const eligible = debits.filter((t) => !isTransferTransaction(t));
    return {
      total: eligible.reduce((sum, t) => sum + Math.abs(t.amount), 0),
      count: eligible.length,
    };
  }, [filteredTransactions]);

  // Actual earliest–latest transaction date among the deposits currently in
  // view (not the filter's start/end). Used on the Total Deposits card so the
  // label reflects the data that is really being summed.
  const depositDateRange = useMemo(() => {
    const depositDates = filteredTransactions
      .filter((t) => t.amount < 0)
      .map((t) => t.date)
      .filter(Boolean)
      .sort();
    if (depositDates.length === 0) {
      return { start: "", end: "", label: "" };
    }
    const start = depositDates[0];
    const end = depositDates[depositDates.length - 1];
    return { start, end, label: formatRangeLabel(start, end) };
  }, [filteredTransactions]);

  useEffect(() => {
    console.log("[DEBUG] Total deposits summary", {
      includeTransfers,
      total: totalDeposits.total,
      count: totalDeposits.count,
      dateRange: depositDateRange.label,
    });
  }, [
    includeTransfers,
    totalDeposits.count,
    totalDeposits.total,
    depositDateRange.label,
  ]);

  useEffect(() => {
    console.log("[DEBUG] Total debits summary", {
      total: totalDebits.total,
      count: totalDebits.count,
    });
  }, [totalDebits.count, totalDebits.total]);

  const allGeneralCategories = Array.from(
    new Set([...GENERAL_CATEGORIES, ...customGeneralCategories])
  ).sort((a, b) => a.localeCompare(b));

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 3 }}>
      {/* AI Processing Status - Fixed at bottom */}
      {aiProcessing && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            backgroundColor: "background.paper",
            borderTop: 2,
            borderColor: "primary.main",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
            animation: "slideUp 0.3s ease-out",
            "@keyframes slideUp": {
              from: {
                transform: "translateY(100%)",
              },
              to: {
                transform: "translateY(0)",
              },
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Container maxWidth="lg">
              <Box sx={{ width: "100%" }}>
                {/* Status Header */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <CircularProgress size={24} thickness={4} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="medium">
                      {aiStatus}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Processing transactions with AI categorization...
                    </Typography>
                  </Box>
                  {/* Close button (for canceling if needed) */}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setAiProcessing(false);
                      setAiProgress(0);
                      setAiStatus("");
                    }}
                    sx={{ ml: 2 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Progress Info */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Progress: {Math.round(aiProgress)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((aiProgress / 100) * transactions.length)} of{" "}
                    {transactions.length} transactions processed
                  </Typography>
                </Box>

                {/* Progress Bar */}
                <LinearProgress
                  variant="determinate"
                  value={aiProgress}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: "action.hover",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      background:
                        "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
                    },
                  }}
                />

                {/* Additional Info */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {aiProgress < 30 && "Initializing AI engine..."}
                    {aiProgress >= 30 &&
                      aiProgress < 50 &&
                      "Analyzing transaction patterns..."}
                    {aiProgress >= 50 &&
                      aiProgress < 80 &&
                      "Applying categorization rules..."}
                    {aiProgress >= 80 &&
                      aiProgress < 100 &&
                      "Saving to database..."}
                    {aiProgress === 100 && "Complete!"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Estimated time remaining:{" "}
                    {Math.max(1, Math.round((100 - aiProgress) / 10))}s
                  </Typography>
                </Box>
              </Box>
            </Container>
          </Box>
        </Box>
      )}

      {/* Success Status */}
      {!aiProcessing && aiStatus && (
        <Alert
          severity={aiStatus.includes("complete") ? "success" : "info"}
          sx={{ mb: 3 }}
          onClose={() => setAiStatus("")}
        >
          {aiStatus}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
          mb: 2,
        }}
      >
        <Card>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Deposits
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {expenseUtils.formatCurrency(totalDeposits.total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalDeposits.count} deposit
                {totalDeposits.count === 1 ? "" : "s"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {depositDateRange.label || formatRangeLabel(startDate, endDate)}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeTransfers}
                  onChange={(event) =>
                    setIncludeTransfers(event.target.checked)
                  }
                />
              }
              label="Include Transfers"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Debits
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {expenseUtils.formatCurrency(totalDebits.total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalDebits.count} debit
                {totalDebits.count === 1 ? "" : "s"} (transfers excluded)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Account</InputLabel>
              <Select
                size="small"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                label="Account"
                startAdornment={
                  <InputAdornment position="start">
                    <AccountBalanceWalletIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.account_id} value={account.account_id}>
                    {customNames[account.account_id] ||
                      `${account.name}${account.mask ? ` (${account.mask})` : ""}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 130 }}
            />

            <TextField
              size="small"
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 130 }}
            />

            <Tooltip
              title="Shared Only"
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000",
                    color: "#fff",
                    fontSize: 12,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                onClick={() => setSharedOnly((prev) => !prev)}
                color={sharedOnly ? "primary" : "default"}
              >
                <FilterAltOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="More filters" placement="top">
              <IconButton
                size="small"
                onClick={() => setShowAdvancedFilters((prev) => !prev)}
                color={
                  showAdvancedFilters || hasActiveAdvancedFilters
                    ? "primary"
                    : "default"
                }
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ ml: "auto" }}>
            <Pagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalItems={filteredTransactions.length}
              onPageChange={setPage}
              onRowsPerPageChange={(newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage);
                setPage(0);
              }}
            />
          </Box>
        </Box>

        <Collapse in={showAdvancedFilters}>
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {/* Saved filter profiles */}
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Profile</InputLabel>
              <Select
                value={activeProfileId}
                label="Profile"
                onChange={(e) => {
                  const prof = filterProfiles.find(
                    (p) => p.id === e.target.value
                  );
                  if (prof) applyFilterProfile(prof);
                  else setActiveProfileId("");
                }}
                startAdornment={
                  <BookmarkBorderIcon fontSize="small" sx={{ mr: 0.5 }} />
                }
              >
                <MenuItem value="">No profile</MenuItem>
                {filterProfiles.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Save current filters as a profile" placement="top">
              <IconButton size="small" onClick={saveFilterProfile}>
                <SavingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {activeProfileId && (
              <Tooltip title="Delete this profile" placement="top">
                <IconButton size="small" onClick={deleteActiveProfile}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Organization</InputLabel>
              <Select
                value={selectedOrganization}
                label="Organization"
                onChange={(e) => setSelectedOrganization(e.target.value)}
              >
                <MenuItem value="">All organizations</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Institution</InputLabel>
              <Select
                value={selectedInstitution}
                label="Institution"
                onChange={(e) => setSelectedInstitution(e.target.value)}
              >
                <MenuItem value="">All institutions</MenuItem>
                {institutionOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={selectedAccountType}
                label="Account Type"
                onChange={(e) => setSelectedAccountType(e.target.value)}
              >
                <MenuItem value="">All types</MenuItem>
                {accountTypeOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {formatDisplayLabel(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Min $"
              type="number"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              sx={{ width: 100 }}
            />
            <TextField
              size="small"
              label="Max $"
              type="number"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              sx={{ width: 100 }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>General Category</InputLabel>
              <Select
                value={filterGeneralCategory}
                label="General Category"
                onChange={(e) => setFilterGeneralCategory(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {allGeneralCategories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {formatDisplayLabel(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Federal Category</InputLabel>
              <Select
                value={filterFederalCategory}
                label="Federal Category"
                onChange={(e) => setFilterFederalCategory(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {US_FEDERAL_TAX_CATEGORIES.map((c) => (
                  <MenuItem key={c.id} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>State Category</InputLabel>
              <Select
                value={filterOregonCategory}
                label="State Category"
                onChange={(e) => setFilterOregonCategory(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {OREGON_STATE_TAX_CATEGORIES.map((c) => (
                  <MenuItem key={c.id} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button size="small" onClick={clearAdvancedFilters}>
              Clear filters
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Transactions Table */}
      <TableContainer component={Paper} sx={{ overflow: "visible" }}>
        <Table
          stickyHeader
          sx={{ "& td, & th": { textAlign: "left", px: 0.5 } }}
        >
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              backgroundColor: "background.paper",
            }}
          >
            <TableRow
              sx={{
                "& th": {
                  whiteSpace: "nowrap",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "background.paper",
                  textAlign: "left",
                  px: 0.5,
                },
              }}
            >
              <TableCell>
                <Tooltip title="Transaction ID">
                  <ReceiptLongIcon fontSize="small" />
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Date" placement="top" arrow>
                  <TableSortLabel
                    active={sortConfig.key === "date"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("date")}
                  >
                    <DateRangeIcon fontSize="small" />
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip
                  title="Synced from Plaid, Yodlee or Manually Imported"
                  placement="top"
                  arrow
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                    }}
                  >
                    <SyncIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Description" placement="top" arrow>
                  <TableSortLabel
                    active={sortConfig.key === "description"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("description")}
                  >
                    <NotesIcon fontSize="small" />
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Institution" placement="top" arrow>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                    }}
                  >
                    <AccountBalanceIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Account Type" placement="top" arrow>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                    }}
                  >
                    <CreditCardIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Account" placement="top" arrow>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                    }}
                  >
                    <SavingsIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Amount" placement="top" arrow>
                  <TableSortLabel
                    active={sortConfig.key === "amount"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("amount")}
                  >
                    <AttachMoneyIcon fontSize="small" />
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Transaction Type" placement="top" arrow>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                    }}
                  >
                    <AccountBalanceWalletIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === "general_category"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("general_category")}
                >
                  <Tooltip title="General Category" placement="top" arrow>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "help",
                      }}
                    >
                      <AutoAwesomeIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === "federal_category"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("federal_category")}
                >
                  <Tooltip title="Federal Tax Category" placement="top" arrow>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "help",
                      }}
                    >
                      <AccountBalanceWalletIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === "oregon_category"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("oregon_category")}
                >
                  <Tooltip title="State Tax Category" placement="top" arrow>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "help",
                      }}
                    >
                      <FilterAltOutlinedIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Tooltip title="AI confidence level for the assigned categories.">
                  <Box
                    component="span"
                    sx={{
                      cursor: "help",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <VerifiedIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Share" placement="top" arrow>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                    }}
                  >
                    <ShareIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Skeleton placeholder rows
              [...Array(1)].map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Skeleton variant="text" width={140} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={140} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="circular" width={20} height={20} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="50%" height={14} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={90} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={110} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={70} sx={{ ml: "auto" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" width={100} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" width={100} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={70} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" width={60} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="circular" width={28} height={28} />
                  </TableCell>
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={14} align="left">
                  <Alert
                    severity="error"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : filteredTransactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} align="left">
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {!cacheStatus.refreshing &&
                  !syncing &&
                  cacheStatus.lastSyncedAt && (
                    <TableRow>
                      <TableCell colSpan={14} align="left">
                        <Typography variant="caption" color="text.secondary">
                          Last synced with your institutions via Plaid on{" "}
                          {formatPlaidSyncTimestamp(cacheStatus.lastSyncedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                {(cacheStatus.refreshing || syncing) && (
                  <>
                    <TableRow>
                      <TableCell colSpan={14} align="left">
                        <Typography variant="caption" color="text.secondary">
                          Syncing with Your Institutions via Plaid.
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Skeleton variant="text" width={140} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={140} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="circular" width={20} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="text" width="50%" height={14} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={90} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={120} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={110} />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          variant="text"
                          width={70}
                          sx={{ ml: "auto" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={120} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rounded" width={100} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rounded" width={100} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={70} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rounded" width={60} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="circular" width={28} height={28} />
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {filteredTransactions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transaction) => {
                    const account = accounts.find(
                      (a) => a.account_id === transaction.account_id
                    );
                    return (
                      <TableRow
                        key={transaction.transaction_id}
                        sx={{
                          "&:hover .transaction-id-copy": {
                            opacity: 1,
                            transform: "translateY(0)",
                          },
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Tooltip
                              title={transaction.transaction_id}
                              onOpen={() =>
                                console.log(
                                  "[DEBUG] Transaction ID tooltip opened:",
                                  {
                                    transactionId: transaction.transaction_id,
                                  }
                                )
                              }
                            >
                              <IconButton
                                size="small"
                                className="transaction-id-copy"
                                onClick={() =>
                                  handleCopyTransactionId(
                                    transaction.transaction_id
                                  )
                                }
                                sx={{
                                  opacity: 0,
                                  transform: "translateY(2px)",
                                  transition:
                                    "opacity 160ms ease, transform 160ms ease",
                                }}
                                aria-label="Copy Transaction ID"
                              >
                                <ReceiptLongIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Box>
                            {(() => {
                              const dateLines = formatTransactionDateLines(
                                transaction.date
                              );
                              return (
                                <>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", fontWeight: 600 }}
                                  >
                                    {dateLines.topLine}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block" }}
                                  >
                                    {dateLines.monthDay}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block" }}
                                  >
                                    {dateLines.year}
                                  </Typography>
                                </>
                              );
                            })()}
                            {formatTransactionTime(transaction) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", fontSize: "0.7rem" }}
                              >
                                {formatTransactionTime(transaction)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {transaction.source === "manual" &&
                          !isPlaidSyncedTransaction(transaction) ? (
                            <Tooltip title="Manually Imported">
                              <ContentCopyIcon
                                fontSize="small"
                                sx={{ color: "text.secondary" }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Synced from Plaid">
                              <Box
                                component="img"
                                src="https://www.logo.wine/a/logo/Plaid_(company)/Plaid_(company)-Logo.wine.svg"
                                alt="Plaid"
                                sx={{
                                  width: 20,
                                  height: 12,
                                  objectFit: "contain",
                                  display: "block",
                                  filter: "grayscale(1)",
                                  opacity: 0.8,
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell
                          onClick={() =>
                            handleStartDescriptionEdit(transaction)
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          {editingDescriptionId ===
                          transaction.transaction_id ? (
                            <ClickAwayListener
                              onClickAway={() => {
                                void handleSaveDescription(transaction);
                              }}
                            >
                              <TextField
                                size="small"
                                autoFocus
                                value={editingDescriptionValue}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) =>
                                  setEditingDescriptionValue(event.target.value)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    void handleSaveDescription(transaction);
                                  }
                                  if (event.key === "Escape") {
                                    event.preventDefault();
                                    handleCancelDescriptionEdit();
                                  }
                                }}
                                placeholder={
                                  transaction.merchant_name ||
                                  transaction.name ||
                                  "Enter description"
                                }
                                fullWidth
                              />
                            </ClickAwayListener>
                          ) : (
                            <Box>
                              {(() => {
                                const displayName =
                                  transaction.custom_description ||
                                  transaction.merchant_name ||
                                  transaction.name;
                                const originalDescription =
                                  getOriginalDescriptionText(transaction);
                                const showOriginal =
                                  Boolean(originalDescription) &&
                                  originalDescription !== displayName;
                                return (
                                  <>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: "0.8rem" }}
                                      >
                                        {displayName}
                                      </Typography>
                                      {(transaction.is_shared ||
                                        transaction.isShared) && (
                                        <Chip
                                          label="Shared"
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                      {transaction.transaction_id &&
                                        debtPaymentsByTxn.has(
                                          transaction.transaction_id
                                        ) && (
                                          <Chip
                                            label={`Debt payment · ${
                                              debtPaymentsByTxn.get(
                                                transaction.transaction_id
                                              )?.debtCreditor ||
                                              debtPaymentsByTxn.get(
                                                transaction.transaction_id
                                              )?.debtAccount
                                            }`}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                          />
                                        )}
                                    </Box>
                                    {showOriginal && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", opacity: 0.6 }}
                                      >
                                        {originalDescription}
                                      </Typography>
                                    )}
                                  </>
                                );
                              })()}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {resolveInstitutionIconSrc(account) ? (
                            <Tooltip
                              title={
                                resolveInstitutionName(account) || "Institution"
                              }
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  "& img": {
                                    width: 28,
                                    height: 28,
                                    objectFit: "contain",
                                    filter: "grayscale(1)",
                                    opacity: 0.75,
                                    transition:
                                      "filter 160ms ease, opacity 160ms ease",
                                  },
                                  "&:hover img": {
                                    filter: "grayscale(0)",
                                    opacity: 1,
                                  },
                                }}
                              >
                                <Box
                                  component="img"
                                  src={resolveInstitutionIconSrc(account)!}
                                  alt={
                                    resolveInstitutionName(account) ||
                                    "Institution"
                                  }
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            // No logo available — fall back to the institution
                            // name (text) so the column isn't just "—".
                            (() => {
                              const instName = resolveInstitutionName(account);
                              return instName ? (
                                <Typography variant="caption">
                                  {normalizeAllCapsWords(instName)}
                                </Typography>
                              ) : (
                                "—"
                              );
                            })()
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDisplayLabel(
                            account?.subtype || account?.type || "unspecified"
                          )}
                        </TableCell>
                        <TableCell>
                          {account
                            ? customNames[account.account_id] ||
                              `${normalizeAllCapsWords(account.name)}${account.mask ? ` (${account.mask})` : ""}`
                            : transaction.account_id}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  maxWidth: 480,
                                  maxHeight: 320,
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontSize: 12,
                                }}
                              >
                                {getPlaidTransactionJson(transaction)}
                              </Box>
                            }
                            onOpen={() =>
                              console.log(
                                "[DEBUG] Transaction JSON tooltip opened:",
                                {
                                  transactionId: transaction.transaction_id,
                                }
                              )
                            }
                          >
                            <Typography
                              component="span"
                              color={
                                transaction.amount < 0
                                  ? "success.main"
                                  : "error.main"
                              }
                              fontWeight="medium"
                              sx={{ cursor: "help" }}
                            >
                              {transaction.amount < 0 ? "+" : "-"}$
                              {Math.abs(transaction.amount).toFixed(2)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {getTransactionTypeLabel(transaction)}
                        </TableCell>
                        <TableCell
                          onClick={() =>
                            setEditingCell({
                              transactionId: transaction.transaction_id,
                              field: "general",
                            })
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          {editingCell?.transactionId ===
                            transaction.transaction_id &&
                          editingCell?.field === "general" ? (
                            <ClickAwayListener
                              onClickAway={() => setEditingCell(null)}
                            >
                              <Autocomplete
                                size="small"
                                options={allGeneralCategories}
                                freeSolo
                                clearOnBlur={false}
                                value={transaction.general_category || null}
                                onChange={async (event, newValue) => {
                                  if (!newValue) {
                                    setEditingCell(null);
                                    return;
                                  }
                                  const normalizedCategory =
                                    typeof newValue === "string"
                                      ? newValue.trim()
                                      : newValue;
                                  if (!normalizedCategory) {
                                    setEditingCell(null);
                                    return;
                                  }
                                  if (
                                    isDebitAmount(transaction.amount) &&
                                    normalizedCategory ===
                                      INCOME_GENERAL_CATEGORY
                                  ) {
                                    setError(
                                      "Debits cannot be categorized as Income."
                                    );
                                    return;
                                  }
                                  const isCustomCategory =
                                    !GENERAL_CATEGORIES.some(
                                      (cat) =>
                                        cat.toLowerCase() ===
                                        normalizedCategory.toLowerCase()
                                    ) &&
                                    !customGeneralCategories.some(
                                      (cat) =>
                                        cat.toLowerCase() ===
                                        normalizedCategory.toLowerCase()
                                    );
                                  if (isCustomCategory) {
                                    const updated = [
                                      ...customGeneralCategories,
                                      normalizedCategory,
                                    ].sort((a, b) => a.localeCompare(b));
                                    await saveCustomGeneralCategories(updated);
                                    console.log(
                                      "[DEBUG] Added custom category from inline edit",
                                      { category: normalizedCategory }
                                    );
                                  }
                                  await transactionCategoriesDB.upsertCategories(
                                    [
                                      {
                                        transaction_id:
                                          transaction.transaction_id,
                                        general_category: normalizedCategory,
                                        category_icon:
                                          getTransactionCategoryIconKey(
                                            normalizedCategory
                                          ) ?? undefined,
                                        federal_category:
                                          transaction.federal_category,
                                        oregon_category:
                                          transaction.oregon_category,
                                        ai_confidence: "user_verified",
                                        ai_processed: true,
                                        needs_user_input: false,
                                      },
                                    ]
                                  );
                                  // Reload transactions to show the update
                                  await loadTransactions();
                                  setEditingCell(null);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    autoFocus
                                    placeholder="Select category"
                                  />
                                )}
                                sx={{ minWidth: 200 }}
                              />
                            </ClickAwayListener>
                          ) : transaction.general_category ? (
                            <Chip
                              label={
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                  }}
                                >
                                  <CategoryIconBadge
                                    iconKey={getTransactionCategoryIconKey(
                                      transaction.general_category
                                    )}
                                    size={14}
                                  />
                                  <span>{transaction.general_category}</span>
                                </Box>
                              }
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : transaction.needs_user_input ? (
                            <Chip
                              label="Needs Input"
                              size="small"
                              color="warning"
                              variant="filled"
                            />
                          ) : (
                            <Chip
                              label="Uncategorized"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell
                          onClick={() =>
                            setEditingCell({
                              transactionId: transaction.transaction_id,
                              field: "federal",
                            })
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          {editingCell?.transactionId ===
                            transaction.transaction_id &&
                          editingCell?.field === "federal" ? (
                            <ClickAwayListener
                              onClickAway={() => setEditingCell(null)}
                            >
                              <Autocomplete
                                size="small"
                                options={US_FEDERAL_TAX_CATEGORIES}
                                value={
                                  US_FEDERAL_TAX_CATEGORIES.find(
                                    (cat) =>
                                      cat.id === transaction.federal_category
                                  ) ||
                                  US_FEDERAL_TAX_CATEGORIES.find(
                                    (cat) =>
                                      cat.name === transaction.federal_category
                                  ) ||
                                  null
                                }
                                getOptionLabel={(option) => option.name}
                                onChange={async (event, newValue) => {
                                  await transactionCategoriesDB.upsertCategories(
                                    [
                                      {
                                        transaction_id:
                                          transaction.transaction_id,
                                        general_category:
                                          transaction.general_category,
                                        category_icon:
                                          getTransactionCategoryIconKey(
                                            transaction.general_category
                                          ) ?? undefined,
                                        federal_category:
                                          newValue?.id || undefined,
                                        oregon_category:
                                          transaction.oregon_category,
                                        ai_confidence: "user_verified",
                                        ai_processed: true,
                                        needs_user_input: false,
                                      },
                                    ]
                                  );
                                  // Reload transactions to show the update
                                  await loadTransactions();
                                  setEditingCell(null);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    autoFocus
                                    placeholder="Select federal category"
                                  />
                                )}
                                sx={{ minWidth: 200 }}
                              />
                            </ClickAwayListener>
                          ) : (
                            transaction.federal_category && (
                              <Chip
                                label={getTaxCategoryName(
                                  transaction.federal_category,
                                  US_FEDERAL_TAX_CATEGORIES
                                )}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            )
                          )}
                        </TableCell>
                        <TableCell
                          onClick={() =>
                            setEditingCell({
                              transactionId: transaction.transaction_id,
                              field: "oregon",
                            })
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          {editingCell?.transactionId ===
                            transaction.transaction_id &&
                          editingCell?.field === "oregon" ? (
                            <ClickAwayListener
                              onClickAway={() => setEditingCell(null)}
                            >
                              <Autocomplete
                                size="small"
                                options={OREGON_STATE_TAX_CATEGORIES}
                                value={
                                  OREGON_STATE_TAX_CATEGORIES.find(
                                    (cat) =>
                                      cat.id === transaction.oregon_category
                                  ) ||
                                  OREGON_STATE_TAX_CATEGORIES.find(
                                    (cat) =>
                                      cat.name === transaction.oregon_category
                                  ) ||
                                  null
                                }
                                getOptionLabel={(option) => option.name}
                                onChange={async (event, newValue) => {
                                  await transactionCategoriesDB.upsertCategories(
                                    [
                                      {
                                        transaction_id:
                                          transaction.transaction_id,
                                        general_category:
                                          transaction.general_category,
                                        category_icon:
                                          getTransactionCategoryIconKey(
                                            transaction.general_category
                                          ) ?? undefined,
                                        federal_category:
                                          transaction.federal_category,
                                        oregon_category:
                                          newValue?.id || undefined,
                                        ai_confidence: "user_verified",
                                        ai_processed: true,
                                        needs_user_input: false,
                                      },
                                    ]
                                  );
                                  // Reload transactions to show the update
                                  await loadTransactions();
                                  setEditingCell(null);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    autoFocus
                                    placeholder="Select Oregon category"
                                  />
                                )}
                                sx={{ minWidth: 200 }}
                              />
                            </ClickAwayListener>
                          ) : (
                            transaction.oregon_category && (
                              <Chip
                                label={getTaxCategoryName(
                                  transaction.oregon_category,
                                  OREGON_STATE_TAX_CATEGORIES
                                )}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            )
                          )}
                        </TableCell>
                        <TableCell>
                          {transaction.ai_confidence && (
                            <Chip
                              label={formatDisplayLabel(
                                transaction.ai_confidence
                              )}
                              size="small"
                              color={
                                transaction.ai_confidence === "high"
                                  ? "success"
                                  : transaction.ai_confidence ===
                                      "user_verified"
                                    ? "success"
                                    : transaction.ai_confidence === "medium"
                                      ? "warning"
                                      : "error"
                              }
                              variant="outlined"
                              icon={
                                transaction.ai_confidence ===
                                "user_verified" ? (
                                  <CheckIcon />
                                ) : undefined
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Share">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleShareTransaction(transaction)
                              }
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <LoadingTimerDebug
        loading={loading}
        label="Transactions"
        details={{
          error,
          categoriesLoading,
          syncing,
          cacheRefreshing: cacheStatus.refreshing,
          cacheLastSyncedAt: cacheStatus.lastSyncedAt,
          cacheReadFailed: cacheStatus.cacheReadFailed,
          transactions: transactions.length,
          filtered: filteredTransactions.length,
          accounts: accounts.length,
          startDate,
          endDate,
          sortBy: `${sortConfig.key}:${sortConfig.direction}`,
          sharedOnly,
          demoMode: demoService.isDemo(),
        }}
      />

      {/* Bottom Pagination */}
      <Paper sx={{ mt: 2 }}>
        <Pagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={filteredTransactions.length}
          onPageChange={setPage}
          onRowsPerPageChange={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setPage(0);
          }}
        />
      </Paper>

      <Snackbar
        open={copyToastOpen}
        onClose={() => setCopyToastOpen(false)}
        autoHideDuration={1600}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={SlideUp}
        message={
          <Fade in={copyToastOpen} timeout={{ enter: 150, exit: 150 }}>
            <Box sx={{ fontWeight: 600 }}>Copied Transaction ID!</Box>
          </Fade>
        }
      />

      {/* User Categorization Modal */}
      <UserCategorizationModal
        open={showTeachingModal}
        transaction={teachingQueue[currentTeachingIndex] || null}
        learnedPatterns={learnedPatterns}
        allTransactions={transactions}
        generalCategories={allGeneralCategories}
        federalCategories={US_FEDERAL_TAX_CATEGORIES.map((cat) => ({
          id: cat.id,
          name: cat.name,
          description: cat.description || "",
        }))}
        oregonCategories={OREGON_STATE_TAX_CATEGORIES.map((cat) => ({
          id: cat.id,
          name: cat.name,
          description: cat.description || "",
        }))}
        onCategorize={handleUserCategorization}
        onSkip={handleSkipTeaching}
        onClose={closeTeachingModal}
        currentIndex={currentTeachingIndex + 1}
        totalCount={teachingQueue.length}
      />
      <ShareRecordModal
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setShareTransaction(null);
        }}
        groups={groups}
        onShare={handleShareRecord}
      />
      <PageShareModal
        open={pageShareOpen}
        onClose={() => setPageShareOpen(false)}
        pageType="transactions"
        payload={{}}
      />

      {/* Floating Action Buttons */}
      <FloatingActionMenu label="Transactions actions">
        <Tooltip
          title={syncing ? "Syncing..." : "Sync Transactions"}
          placement="left"
          arrow
        >
          <span>
            <Fab
              color="info"
              size="large"
              onClick={handleSyncTransactions}
              disabled={syncing}
              sx={{ opacity: syncing ? 0.6 : 1 }}
            >
              {syncing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <SyncIcon />
              )}
            </Fab>
          </span>
        </Tooltip>

        {/* Clear All Categories FAB */}
        <Tooltip title="Clear All Categories" placement="left" arrow>
          <span>
            <Fab
              color="error"
              size="large"
              onClick={handleClearAllCategories}
              disabled={aiProcessing || transactions.length === 0}
              sx={{
                opacity: aiProcessing || transactions.length === 0 ? 0.5 : 1,
                ...(theme.palette.mode === "dark" && {
                  bgcolor: theme.palette.error.dark,
                  "&:hover": {
                    bgcolor: theme.palette.error.main,
                  },
                }),
              }}
            >
              <ClearIcon />
            </Fab>
          </span>
        </Tooltip>

        {/* Smart Categorize All FAB */}
        <Box sx={{ position: "relative" }}>
          <Tooltip
            title={aiProcessing ? "Processing..." : "Smart Categorize All"}
            placement="left"
            arrow
          >
            <span>
              <Fab
                color="primary"
                size="large"
                onClick={handleSmartCategorizeAll}
                disabled={aiProcessing || transactions.length === 0}
                sx={{
                  opacity: aiProcessing || transactions.length === 0 ? 0.5 : 1,
                }}
              >
                {aiProcessing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )}
              </Fab>
            </span>
          </Tooltip>

          {/* Demo Mode Floating Tooltip */}
          {isDemoMode && showDemoTooltip && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: "calc(100% + 16px)",
                transform: "translateY(0)",
                zIndex: 1001,
                animation: "breathe 2s ease-in-out infinite",
                "@keyframes breathe": {
                  "0%, 100%": {
                    transform: "translateY(0) scale(1)",
                    opacity: 1,
                  },
                  "50%": {
                    transform: "translateY(0) scale(1.02)",
                    opacity: 0.9,
                  },
                },
              }}
            >
              {/* Arrow pointing right */}
              <Box
                sx={{
                  position: "absolute",
                  top: "calc(100% - 28px)",
                  right: 0,
                  transform: "translateY(-50%)",
                  width: 0,
                  height: 0,
                  borderTop: "10px solid transparent",
                  borderBottom: "10px solid transparent",
                  borderLeft: "16px solid #1a1a1a",
                }}
              />
              {/* Tooltip content */}
              <Paper
                elevation={8}
                sx={{
                  p: 2,
                  maxWidth: 280,
                  maxHeight: "min(240px, calc(100vh - 120px))",
                  bgcolor: "#1a1a1a",
                  color: "#ffffff",
                  borderRadius: 2,
                  position: "relative",
                  overflowY: "auto",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setShowDemoTooltip(false)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    color: "#ffffff",
                    opacity: 0.7,
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <AutoAwesomeIcon sx={{ mt: 0.5, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ pr: 2 }}>
                    Automagically categorize all of your transactions and teach
                    the AI how to do it better in the future at the same time.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      </FloatingActionMenu>
    </Container>
  );
};

export default TransactionsPage;
